import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";

const line = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1.1, delay: 0.3 + i * 0.15, ease: [0.16, 1, 0.3, 1] },
  }),
};

const HERO_IMG = "/api/media/hero-mastro-birraio.jpeg";

export const Hero = () => {
  const ref = useRef(null);
  const navigate = useNavigate();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const scrollToCatalog = () => navigate("/catalogo");

  return (
    <section
      id="hero"
      ref={ref}
      className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-20"
      data-testid="hero-section"
    >
      {/* Parallax bottle */}
      <motion.div
        style={{ y, scale }}
        className="absolute right-0 top-0 h-full w-full md:w-1/2 pointer-events-none"
      >
        <div className="relative h-full w-full">
          <img
            src={HERO_IMG}
            alt="Il mastro birraio del Birrificio Vecchio Cinghiale nel suo birrificio tradizionale — illustrazione stile Studio Ghibli"
            className="h-full w-full object-cover object-center opacity-70"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0D110D] via-[#0D110D]/40 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0D110D] via-transparent to-[#0D110D]/60" />
        </div>
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 max-w-[1600px] mx-auto w-full px-6 lg:px-12">
        <motion.p
          initial={false}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 1 }}
          className="font-mono-accent text-xs md:text-sm uppercase tracking-[0.3em] text-[#D47738] mb-8"
          data-testid="hero-overline"
        >
          Vecchio Cinghiale — Est. 2025
        </motion.p>

        <h1 className="font-serif-display font-medium leading-[0.85] tracking-tighter text-[#E8E2D2]">
          {["La Passione", "per la", "Birra"].map((word, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                custom={i}
                variants={line}
                initial={false}
                animate="visible"
                className="block text-6xl sm:text-7xl md:text-8xl lg:text-[10rem]"
              >
                {i === 2 ? (
                  <span className="italic text-[#D47738]">{word}</span>
                ) : (
                  word
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 1 }}
          className="mt-10 max-w-md"
        >
          <p className="text-base md:text-lg text-[#E8E2D2]/70 leading-relaxed">
            Una produzione che si prende il suo tempo, sia per le materie prime
            che per la lavorazione: nessun compromesso. Le nostre birre nascono
            dalla pazienza e dalla cura.
          </p>
          <button
            onClick={scrollToCatalog}
            className="mt-8 bg-[#D47738] text-[#0D110D] px-10 py-4 font-mono-accent text-xs uppercase tracking-[0.2em] hover:bg-[#E8E2D2] transition-colors duration-300"
            data-testid="hero-cta"
          >
            Scopri le Birre
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={false}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 font-mono-accent text-[10px] uppercase tracking-[0.3em] text-[#E8E2D2]/40"
      >
        Scorri
      </motion.div>
    </section>
  );
};
